import React, { useEffect, useMemo, useState } from 'react';
import { useAuthNavigation } from '../../shared/navigation/useAuthNavigation';
import { View, Text, StyleSheet, Image } from 'react-native';

import { appTheme } from '../../shared/theme';
import type { ThemeColors } from '../../shared/theme';
import { useRtl } from '../../shared/i18n/useRtl';
import { DASH_STUDIO_INNER, DASH_STUDIO_RADIUS } from '../../shared/theme/studio/dashboardConstants';
import { Clickable, PRESS_PRESETS } from '../../components/common/Clickable';
import type { CommunityPost } from '../../data/domains/community';
import { SvgIcon } from '../../components/common/SvgIcon';
import { useTranslation } from '../../shared/i18n';
import { HorizontalLtrScrollView } from '../../components/ui/HorizontalLtrScrollView';
import { resolveImageUri } from '../../shared/utils/resolveImageUri';
import { ContentCard } from '../../components/common/ContentCard';
import { dashboardSet1CardShell, dashboardNestedTileShadow } from './variants/set1/dashboardSet1Styles';

const TILE_WIDTH = 132;
const TILE_IMAGE_HEIGHT = 118;
const TILE_CAPTION_MIN_H = 44;
const COMM_TILE_R = 18;

export interface CommunityPostTileProps {
  post: CommunityPost;
  colors: ThemeColors;
  onPress?: () => void;
  style?: object;
  communityPostLabel?: string;
}

export const CommunityPostTile = React.memo(function CommunityPostTile({ post, colors, onPress, style: style_override, communityPostLabel }: CommunityPostTileProps) {
  const [image_failed, set_image_failed] = useState(false);
  const rtl = useRtl();
  const resolved_uri = useMemo(() => resolveImageUri(post.attachment), [post.attachment]);
  const show_image = resolved_uri != null && !image_failed;

  useEffect(() => {
    set_image_failed(false);
  }, [resolved_uri]);

  return (
    <Clickable
      onPress={onPress}
      style={[
        styles.tile,
        dashboardNestedTileShadow(colors),
        {
          backgroundColor: colors.surfaceElevated,
          borderRadius: COMM_TILE_R,
        },
        style_override,
      ]}
      pressScale={PRESS_PRESETS.listRow.scale}
      haptics
      accessibilityLabel={post.content?.slice(0, 50) ?? communityPostLabel}
      accessibilityRole="button">
      <View style={[styles.tileShell, { backgroundColor: colors.surfaceElevated, borderRadius: COMM_TILE_R }]}>
        <View style={[styles.tileImageWrap, { backgroundColor: colors.surfaceElevated }]}>
          {show_image ? (
            <Image
              source={{ uri: resolved_uri! }}
              style={styles.tileImage}
              resizeMode="cover"
              fadeDuration={0}
              onError={() => set_image_failed(true)}
            />
          ) : (
            <View style={[styles.tilePlaceholder, { backgroundColor: colors.primaryUltra || colors.primaryFade }]}>
              <SvgIcon name="chat" size={32} color={colors.primary} />
            </View>
          )}
        </View>
        <View style={[styles.tileCaptionBar, { backgroundColor: colors.surfaceElevated }]}>
          <Text
            style={[
              styles.tileCaption,
              { color: colors.text, textAlign: rtl.textAlign, writingDirection: rtl.writingDirection },
            ]}
            numberOfLines={3}>
            {post.content || ' '}
          </Text>
        </View>
      </View>
    </Clickable>
  );
});

interface DashboardCommunityStripProps {
  posts: CommunityPost[];
  colors: ThemeColors;
}

export function DashboardCommunityStrip({ posts, colors }: DashboardCommunityStripProps) {
  const { t } = useTranslation();
  const rtl = useRtl();
  const navigation = useAuthNavigation();
  const last_three = posts.slice(0, 3);
  const community_title = t('community.title');
  const open_community_label = t('community.openCommunity');
  const community_post_label = t('community.communityPost');
  const no_posts_tap = t('community.noPostsTapToOpenCommunity');
  const text_align: 'left' | 'right' = rtl.isRTL ? 'right' : 'left';
  const card_padding = appTheme.card.padding.lg;

  const go_to_community = () => {
    navigation.navigate('Community');
  };

  if (last_three.length === 0) {
    return (
      <ContentCard
        variant="outlined"
        borderless
        padding="lg"
        style={[
          dashboardSet1CardShell(colors),
          { padding: 0, minWidth: 0, overflow: 'visible', borderRadius: DASH_STUDIO_RADIUS },
        ]}>
        <View
          style={[
            styles.titleWrap,
            { paddingHorizontal: DASH_STUDIO_INNER, paddingTop: card_padding, paddingBottom: appTheme.spacing.sm },
          ]}>
          <Clickable
            onPress={go_to_community}
            accessibilityLabel={community_title}
            accessibilityRole="button"
            pressScale={PRESS_PRESETS.listRow.scale}
            haptics
            contentStyle={styles.titleRowClick}>
            <View style={styles.titleRow}>
              <Text
                style={[
                  styles.communityHeadTitle,
                  { color: colors.textLight, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
                ]}
                numberOfLines={1}>
                {community_title}
              </Text>
              <Text
                style={[styles.titleLink, { color: colors.primary, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' }]}
                numberOfLines={1}>
                {t('dashboard.viewAll')}
              </Text>
            </View>
          </Clickable>
        </View>
        <Clickable
          onPress={go_to_community}
          accessibilityLabel={open_community_label}
          accessibilityRole="button"
          pressScale={PRESS_PRESETS.listRow.scale}
          haptics>
          <View
            style={[
              styles.emptyWrap,
              {
                backgroundColor: colors.surfaceElevated,
                marginHorizontal: DASH_STUDIO_INNER,
                marginTop: 0,
                marginBottom: card_padding + 8,
                borderRadius: 18,
                ...dashboardNestedTileShadow(colors),
              },
            ]}>
            <SvgIcon name="chat" size={32} color={colors.primary} />
            <Text
              style={[
                styles.emptyText,
                { color: colors.textSecondary, textAlign: 'center', writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
              ]}>
              {no_posts_tap}
            </Text>
          </View>
        </Clickable>
      </ContentCard>
    );
  }

  return (
    <ContentCard
      variant="outlined"
      borderless
      padding="lg"
      style={[
        dashboardSet1CardShell(colors),
        { padding: 0, minWidth: 0, overflow: 'visible', borderRadius: DASH_STUDIO_RADIUS },
      ]}>
      <View
        style={[
          styles.titleWrap,
          { paddingHorizontal: DASH_STUDIO_INNER, paddingTop: card_padding, paddingBottom: appTheme.spacing.sm },
        ]}>
        <Clickable
          onPress={go_to_community}
          accessibilityLabel={community_title}
          accessibilityRole="button"
          pressScale={PRESS_PRESETS.listRow.scale}
          haptics
          contentStyle={styles.titleRowClick}>
          <View style={styles.titleRow}>
            <Text
              style={[
                styles.communityHeadTitle,
                { color: colors.textLight, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
              ]}
              numberOfLines={1}>
              {community_title}
            </Text>
            <Text
              style={[styles.titleLink, { color: colors.primary, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' }]}
              numberOfLines={1}>
              {t('dashboard.viewAll')}
            </Text>
          </View>
        </Clickable>
      </View>
      <HorizontalLtrScrollView
        style={styles.scroll}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingHorizontal: DASH_STUDIO_INNER,
            paddingTop: 4,
            paddingBottom: card_padding + 8,
            alignItems: 'flex-start',
          },
        ]}>
        {last_three.map(post => (
          <CommunityPostTile key={post.id} post={post} colors={colors} onPress={go_to_community} communityPostLabel={community_post_label} />
        ))}
      </HorizontalLtrScrollView>
    </ContentCard>
  );
}

const styles = StyleSheet.create({
  titleWrap: {},
  titleRowClick: {
    width: '100%' as const,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: appTheme.spacing.sm,
  },
  communityHeadTitle: {
    ...appTheme.typography.captionSmall,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.9,
    textTransform: 'uppercase' as const,
    flex: 1,
    minWidth: 0,
  },
  titleLink: {
    fontSize: 13,
    fontWeight: '800',
    flexShrink: 0,
  },
  card: {
    padding: 0,
    minWidth: 0,
    overflow: 'hidden',
  },
  emptyWrap: {
    paddingVertical: appTheme.spacing.xl + 4,
    paddingHorizontal: appTheme.spacing.lg,
    minHeight: 128,
    borderRadius: appTheme.card.radius,
    alignItems: 'center',
    justifyContent: 'center',
    gap: appTheme.spacing.md,
  },
  emptyText: {
    ...appTheme.typography.bodySmall,
    fontSize: 13,
    lineHeight: 20,
    textAlign: 'center',
    maxWidth: 280,
  },
  scroll: {
    margin: 0,
    padding: 0,
  },
  scrollContent: {
    gap: appTheme.spacing.md,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  tile: {
    width: TILE_WIDTH,
    overflow: 'visible',
  },
  tileShell: {
    width: TILE_WIDTH,
    overflow: 'hidden',
  },
  tileImageWrap: {
    width: TILE_WIDTH,
    height: TILE_IMAGE_HEIGHT,
    overflow: 'hidden',
    borderTopLeftRadius: COMM_TILE_R,
    borderTopRightRadius: COMM_TILE_R,
  },
  tileImage: {
    width: '100%',
    height: '100%',
  },
  tilePlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileCaptionBar: {
    minHeight: TILE_CAPTION_MIN_H,
    paddingHorizontal: 10,
    paddingVertical: 10,
    justifyContent: 'center',
    borderBottomLeftRadius: COMM_TILE_R,
    borderBottomRightRadius: COMM_TILE_R,
  },
  tileCaption: {
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 15,
  },
});
